if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();
        this.handleInlineError();
        this.addResolved = false;

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            // JSON parsed: any later throw is a render problem, not a failed add.
            this.addResolved = true;

            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.handleInlineError(response.description, response.status);

              // Shopify clamps over-stock adds to the available quantity and STILL
              // returns 422 ("Only N items were added ..."). Items genuinely went
              // into the cart, so open the panel as a normal add would. The inline
              // message stays visible beneath the button. Hard rejects fall through.
              if (this.isClampedAdd(response.description)) this.openPanelForClampedAdd(formData.get('id'));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            // Non-JSON response (e.g. 429 rate limit HTML page) or network failure:
            // the add did not happen, so tell the customer instead of failing silently.
            if (!this.addResolved) this.handleInlineError(null, 'network');
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      /* ── EAC inline error (.csc-card__error in eac-condition-form.liquid) ──────
         Shown beneath the Add to Cart button on card forms that have no Dawn
         error wrapper. Cleared on the next submit, on quantity/condition change
         (see eac-card.js), and automatically after 8s so it never lingers.   */

      handleInlineError(description = null, status = null) {
        const el = this.querySelector('.csc-card__error');
        if (!el) return;

        clearTimeout(this.inlineErrorTimer);

        if (!description && !status) {
          el.classList.remove('is-visible');
          el.textContent = '';
          return;
        }

        el.textContent = this.formatInlineError(description);
        el.classList.add('is-visible');
        this.inlineErrorTimer = setTimeout(() => this.handleInlineError(), 8000);
      }

      formatInlineError(description) {
        const text = String(description || '').trim();

        // Exact stock is known client-side only when the row is not capped at "10+".
        // data-qty derives from variant.inventory_quantity (never .available).
        const row = this.querySelector('.csc-cond-row.selected');
        const exactStock =
          row && row.dataset.qty && !row.dataset.qtyPlus ? parseInt(row.dataset.qty, 10) : null;

        // "Only N items were added to your cart due to availability." — clamped add
        const clamped = text.match(/^Only (\d+) items? (?:were|was) added/i);
        if (clamped) {
          const n = parseInt(clamped[1], 10);
          return `Only ${n} in stock — all ${n} are now in your cart`;
        }

        // "The maximum quantity of this item is already in your cart." — hard reject
        if (/maximum quantity[^.]*already in your cart/i.test(text)) {
          return exactStock
            ? `All ${exactStock} in stock are already in your cart`
            : 'The maximum available is already in your cart';
        }

        if (!text) return 'Could not add to cart — please try again';

        if (exactStock && /stock|inventory|availab|quantity/i.test(text)) {
          return `Only ${exactStock} in stock`;
        }

        return text;
      }

      isClampedAdd(description) {
        return /^Only \d+ items? (?:were|was) added/i.test(String(description || ''));
      }

      /* A clamped add has changed the cart, but Shopify's 422 body carries no
         line key and no rendered sections, so renderContents() cannot be fed
         the error response. Fetch the live cart (for the key) and the panel's
         sections in parallel, then open the panel through the exact same
         renderContents() path a successful add uses — including its
         "already open → update in place" handling. */
      openPanelForClampedAdd(variantId) {
        if (!this.cart || typeof this.cart.renderContents !== 'function') {
          this.refreshCartBubble();
          return;
        }

        const sectionIds = this.cart.getSectionsToRender().map((section) => section.id);

        Promise.all([
          fetch(`${routes.cart_url}.js`).then((response) => response.json()),
          fetch(`${routes.cart_url}?sections=${sectionIds.join(',')}`).then((response) => response.json()),
        ])
          .then(([cart, sections]) => {
            const line = cart.items.find((item) => String(item.variant_id) === String(variantId));
            if (!line) {
              this.refreshCartBubble();
              return;
            }

            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: variantId,
              cartData: cart,
            });

            this.cart.renderContents({ key: line.key, sections });
          })
          .catch(() => this.refreshCartBubble());
      }

      refreshCartBubble() {
        const bubble = document.getElementById('cart-icon-bubble');
        if (!bubble) return;
        fetch(`${routes.cart_url}?sections=cart-icon-bubble`)
          .then((response) => response.json())
          .then((sections) => {
            const section = new DOMParser()
              .parseFromString(sections['cart-icon-bubble'], 'text/html')
              .querySelector('.shopify-section');
            if (section) bubble.innerHTML = section.innerHTML;
          })
          .catch(() => {});
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
